import { DownOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import { AppConfirmation } from '@nxsys/components/AppConfirmationModal';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import {
  PAYMENT_MODEL_ENUM,
  getIrelandPaymentModels,
} from '@nxsys/constants/AppConstants';
import {
  useConfirmMultipleDraftPayroll,
  useDeleteDraftPayrollById,
  useVerifyPayslip,
  useViewDraftPayslipById,
} from '@nxsys/hooks/api/payroll';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { reportActions } from '@nxsys/store/slices/report/reducer/reportSlice';
import { GetDraftPayrollListParams } from '@nxsys/store/slices/report/types';
import { DraftPayroll } from '@nxsys/types/models/payroll';
import { showCurrencyByCountry } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { formatMoney } from '@nxsys/utils/number-helper';
import {
  getDraftPayrollBureauColumns,
  getDraftPayrollPayeUmbrellaColumns,
} from '../../reports/payroll/listing/columns';
import { verifierColumn } from '../../reports/payroll/listing/verifierColumn';
import PayslipVerdictModal from '../../reports/payroll/listing/PayslipVerdictModal';
import { Dropdown, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { debounce, sumBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import usePayrollSocket from '@nxsys/hooks/usePayrollSocket';
import { useDispatch, useSelector } from 'react-redux';

type Props = {
  cbAfterAction?: () => void;
};

const ConfirmPayroll: React.FC<Props> = ({ cbAfterAction = () => {} }) => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();
  const { isBureau, payrollCountry, user } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  // Ireland's models depend on the product too: bureau has PAYE + RCT, umbrella
  // adds UMBRELLA. Hardcoding the bureau pair here filtered every Irish
  // umbrella payroll out of this surface's counts and lists.
  const validPaymentModels = isIreland
    ? getIrelandPaymentModels(isBureau)
    : isBureau
      ? [PAYMENT_MODEL_ENUM.PAYEE]
      : [
          PAYMENT_MODEL_ENUM.CIS,
          PAYMENT_MODEL_ENUM.NON_CIS,
          PAYMENT_MODEL_ENUM.UMBRELLA,
          PAYMENT_MODEL_ENUM.PAYEE,
          PAYMENT_MODEL_ENUM.HYBRID,
          PAYMENT_MODEL_ENUM.PAYE_HYBRID,
          PAYMENT_MODEL_ENUM.PAYE_FTH,
        ];

  const dispatch = useDispatch();
  const { draftPayrollList } = useSelector(
    (state: AppState) => state.reportStore,
  );
  const { list, pagination, loading, queryParams } = draftPayrollList;

  const [selectedRows, setSelectedRows] = useState<DraftPayroll[]>([]);
  // NX-4971 — advisory payslip verifier (view verdict detail / re-run).
  const [verdictRecord, setVerdictRecord] = useState<DraftPayroll | null>(null);
  const [rerunningIds, setRerunningIds] = useState<string[]>([]);
  const { run: verifyPayslip, loading: isVerifyLoading } =
    useVerifyPayslip();
  const displayList = useMemo(
    () =>
      (list ?? []).map((row: DraftPayroll) =>
        rerunningIds.includes(row.id)
          ? {
              ...row,
              payslipVerificationRef: {
                ...row.payslipVerificationRef,
                status: 'RUNNING' as const,
                verdict: null,
              },
            }
          : row,
      ),
    [list, rerunningIds],
  );
  usePayrollSocket({
    eventName: `payslip-verify-${user?.id}`,
    onSuccess: () => {
      setRerunningIds([]);
      refetchData();
    },
  });
  const handleViewVerdict = (record: DraftPayroll) => setVerdictRecord(record);
  const handleRerunVerification = (record: DraftPayroll) => {
    setVerdictRecord(null);
    setRerunningIds((prev) =>
      prev.includes(record.id) ? prev : [...prev, record.id],
    );
    verifyPayslip({ draftPayrollIds: [record.id] })
      .then((res) => {
        if (res?.success) {
          showMessage({
            type: 'info',
            content:
              'Re-verification requested. The verdict will update shortly.',
          });
        } else {
          setRerunningIds((prev) => prev.filter((id) => id !== record.id));
        }
      })
      .catch(() => {
        setRerunningIds((prev) => prev.filter((id) => id !== record.id));
      });
  };

  const renderTitle = useCallback(() => {
    return <AppPageTitle prefix="Confirm Payroll" separator="" title="" />;
  }, []);

  const renderRightContent = () => {
    const menuProps = {
      items: [
        {
          key: 'addPayment',
          label: messages['common.addPayment'],
          onClick: () => navigate('/payments'),
        },
        {
          key: 'addDeduction',
          label: messages['common.addDeduction'],
          onClick: () => navigate('/deductions'),
        },
      ],
    };
    return (
      <div className="flex items-center gap-x-2 mr-[48px]">
        <AppButton
          type="primary"
          name="Confirm Payroll"
          onClick={handleConfirmSelectedDraftPayroll}
        />
        <Dropdown menu={menuProps}>
          <AppButton
            variant="outlined"
            name={messages['common.action']}
            rightIcon={<DownOutlined />}
          />
        </Dropdown>
      </div>
    );
  };

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
    });
  }, []);

  const handleFetchData = (params: GetDraftPayrollListParams) => {
    const newParams = {
      ...params,
      paymentModels: validPaymentModels,
    };

    dispatch(reportActions.getDraftPayrollList(newParams));
  };

  const refetchData = () => {
    handleFetchData({
      ...queryParams,
    });
    cbAfterAction();
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const handleFilter = (params: Partial<GetDraftPayrollListParams>) => {
    handleFetchData({
      ...queryParams,
      ...params,
    });
  };

  const totalTimesheetValuesSelected = selectedRows.length
    ? sumBy(selectedRows, (item: DraftPayroll) => Number(item.timesheetValue))
    : 0;

  const { run: viewDraftPayslipById, loading: isViewDraftPayslipByIdLoading } =
    useViewDraftPayslipById();
  const handleViewDraftPayslipById = (id: string) => {
    viewDraftPayslipById(id).then((res) => {
      if (res.success) {
        window.open(res.data, '_blank');
      } else {
        showMessage({
          type: 'error',
          content: messages['common.somethingWentWrong'],
        });
      }
    });
  };

  const {
    run: confirmMultipleDraftPayroll,
    loading: isConfirmMultipleDraftPayrollLoading,
  } = useConfirmMultipleDraftPayroll();
  const handleConfirmMultipleDraftPayroll = (ids: string[]) => {
    AppConfirmation.open({
      title: messages['common.confirmDraftPayroll'],
      paragraph: messages['common.areYouSureYouWantToConfirm'],
      type: 'warning',
      icon: 'IconWarning',
      okText: messages['common.confirm'],
      onConfirm: () => {
        confirmMultipleDraftPayroll(ids).then((res) => {
          if (res.success) {
            showMessage({
              type: 'success',
              content: messages['common.draftPayrollConfirmed'],
            });
            refetchData();
          }
        });
      },
    });
  };
  const handleConfirmDraftPayroll = (id: string) => {
    handleConfirmMultipleDraftPayroll([id]);
  };
  const handleConfirmSelectedDraftPayroll = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleConfirmMultipleDraftPayroll(selectedRows.map((item) => item.id));
  };

  const {
    run: deleteDraftPayrollById,
    loading: isDeleteDraftPayrollByIdLoading,
  } = useDeleteDraftPayrollById();
  const handleDeleteDraftPayroll = (id: string) => {
    AppConfirmation.open({
      title: messages['common.deleteDraftPayroll'],
      paragraph: messages['common.areYouSureYouWantToDelete'],
      type: 'warning',
      icon: 'IconWarning',
      okText: messages['common.delete'],
      onConfirm: () => {
        deleteDraftPayrollById(id).then((res) => {
          if (res.success) {
            showMessage({
              type: 'success',
              content: messages['common.draftPayrollDeleted'],
            });
            refetchData();
          }
        });
      },
    });
  };

  // Dashboard Confirm Payroll shows draft values, so it reuses the Draft
  // Payroll report columns verbatim — same set, same order, same actions.
  const reportColumns: ColumnsType<DraftPayroll> = isBureau
    ? getDraftPayrollBureauColumns({
        messages,
        payrollCountry,
        handleViewDraftPayslipById,
        handleConfirmDraftPayroll,
        handleDeleteDraftPayroll,
      })
    : getDraftPayrollPayeUmbrellaColumns({
        messages,
        payrollCountry,
        handleViewDraftPayslipById,
        handleConfirmDraftPayroll,
        handleDeleteDraftPayroll,
        isBureau,
      });

  // Verifier is dashboard-only (super admin) and sits right after the actions
  // column, as it did before the report columns were reused.
  const columns: ColumnsType<DraftPayroll> = user?.isSuperAdmin
    ? [
        ...reportColumns.slice(0, 1),
        verifierColumn(messages, handleViewVerdict),
        ...reportColumns.slice(1),
      ]
    : reportColumns;

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={
        isViewDraftPayslipByIdLoading ||
        isConfirmMultipleDraftPayrollLoading ||
        isDeleteDraftPayrollByIdLoading
      }
    >
      <div className="flex justify-between w-full gap-4 flex-wrap mb-[20px]">
        <div className="flex flex-wrap gap-4 flex-1">
          <AppSelectSync
            type="company"
            apiType="searchCompany"
            placeholder="Select Payment Company"
            mode="multiple"
            onChangeValue={(value: any) => {
              handleFilter({
                companyRefs: value?.map((item: any) => item.value) ?? undefined,
              });
            }}
            paramFilter={{ paymentModels: validPaymentModels }}
            className="!w-[260px]"
          />
          <AppSelectSync
            placeholder="Select Agency"
            apiType="searchAgency"
            type="agency"
            mode="multiple"
            onChangeValue={(value: any) => {
              handleFilter({
                agencyRefs: value?.map((item: any) => item.value) ?? undefined,
              });
            }}
            paramFilter={{ paymentModels: validPaymentModels }}
            className="!w-[165px]"
          />
        </div>
        <div className="flex flex-wrap gap-4 items-center">
          {selectedRows.length > 0 && (
            <Typography.Text>
              {messages['common.totalTimesheetValue']}:{' '}
              {formatMoney(
                totalTimesheetValuesSelected,
                showCurrencyByCountry(payrollCountry),
              )}
            </Typography.Text>
          )}
          <AppInputSearch onChange={handleSearch} />
        </div>
      </div>
      <AppsContent>
        <AppTableContainer
          rowKey="id"
          data={displayList}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 'max-content', y: 700 }}
          columns={columns}
          onChangePagination={onChangePagination}
          rowSelection={{
            type: 'checkbox',
            onChange: (_: React.Key[], selectedRows: any[]) => {
              setSelectedRows(selectedRows);
            },
          }}
        />
      </AppsContent>
      <PayslipVerdictModal
        isOpen={!!verdictRecord}
        toggle={() => setVerdictRecord(null)}
        record={verdictRecord}
        onRerun={handleRerunVerification}
        rerunLoading={isVerifyLoading}
        onSaved={refetchData}
      />
    </AppsContainer>
  );
};

export default ConfirmPayroll;
